import { useEffect, useMemo, useState } from 'react';

import { userService } from '@chartai/firebase';
import { type UserLimits } from '@chartai/types';
import { type UsageSummary, deriveUsage, isRecordingBlocked, parseLimits } from '@chartai/utils';

interface UserLimitsResult extends UsageSummary {
  limits: UserLimits;
  /**
   * CAB-605 — may this user start a recording?
   *
   * Derived the same way the Firestore rule is, NOT read from
   * `limits.recordingBlocked`. Always check `isLoading` first: this is `false`
   * while loading, so acting on it too early would let a blocked user through
   * — harmless, since the server refuses anyway — but rendering a blocked
   * state too early would show "out of minutes" to a paying user on first
   * paint, which is not.
   */
  isBlocked: boolean;
  isLoading: boolean;
}

/**
 * CAB-603 — the signed-in user's quota and usage for the current period.
 *
 * Reads `users/{uid}/limits/current`, which only Cloud Functions can write and
 * every client can only read. This is the number to display; the server is what
 * enforces it (CAB-605).
 *
 * Fails closed everywhere — signed out, still loading, or no document at all
 * yields free tier with the free allowance, never unlimited. Check `isLoading`
 * before rendering an "out of minutes" state, or a paying user sees it on first
 * paint. All the parsing rules live in `@chartai/utils/limits`, where they are
 * unit-tested; this hook is only the Firestore subscription.
 */
export function useUserLimits(uid: string | null): UserLimitsResult {
  const [raw, setRaw] = useState<Record<string, unknown> | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!uid) {
      setRaw(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const unsubscribe = userService.subscribeToUserLimits(uid, (data) => {
      setRaw(data);
      setIsLoading(false);
    });

    return unsubscribe;
  }, [uid]);

  return useMemo(() => {
    const limits = parseLimits(raw);
    return {
      limits,
      ...deriveUsage(limits),
      isBlocked: isLoading ? false : isRecordingBlocked(limits),
      isLoading,
    };
  }, [raw, isLoading]);
}
